import { ReactNode, useState, useRef, useEffect } from "react";
import cfVerticalLogo from "@/assets/cf-vertical.svg";
import { useLocation, useParams } from "react-router-dom";
import { LocaleLink } from "@/components/LocaleLink";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  KeyRound,
  X,
  ArrowRightLeft,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────
export interface SidebarNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

export interface SidebarCta {
  label: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
}

interface PortalSidebarProps {
  /** "Operator Portal" or "Handwerker Portal" */
  portalLabel: string;
  /** Role label shown below user name */
  roleLabel: string;
  /** Nav items */
  navItems: SidebarNavItem[];
  /** Base path used for logo link (e.g. "/operator" or "/dashboard") */
  homePath: string;
  /** Optional CTA card */
  cta?: SidebarCta;
  /** Profile path */
  profilePath?: string;
  /** Link to switch portal (e.g. "/dashboard" from operator, "/operator" from provider) */
  portalSwitchPath?: string;
  portalSwitchLabel?: string;
  /** Extra content below nav */
  children?: ReactNode;
}

export default function PortalSidebar({
  portalLabel,
  roleLabel,
  navItems,
  homePath,
  cta,
  profilePath,
  portalSwitchPath,
  portalSwitchLabel,
}: PortalSidebarProps) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useLocaleNavigate();
  const location = useLocation();
  const params = useParams<{ locale: string }>();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const avatarRef = useRef<HTMLDivElement>(null);

  // Close avatar dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) return;
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setShowPasswordModal(false);
      setNewPassword("");
    } catch (err: any) {
      console.error("Password update error:", err);
    } finally {
      setPasswordSaving(false);
    }
  };

  const initials = user?.email?.slice(0, 2).toUpperCase() || "??";
  const displayName = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "Benutzer";

  // Build locale-aware full path for active check
  const localePrefix = params.locale || "de";
  const withLocale = (p: string) => p.startsWith("/") ? `/${localePrefix}${p}` : p;

  const isActive = (path: string) => {
    const fullPath = withLocale(path);
    const fullHome = withLocale(homePath);
    return location.pathname === fullPath ||
      (fullPath !== fullHome && location.pathname.startsWith(fullPath));
  };

  // ── Shared sidebar content ────────────────────────────
  const sidebarContent = (isDesktop: boolean) => (
    <div className="flex flex-col h-full">
      {/* 1️⃣ Header */}
      <div className="p-5 border-b border-border relative">
        {/* Close / Collapse – top right */}
        {isDesktop ? (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute top-2 right-2 p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        ) : (
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-2 right-2 p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <LocaleLink to={homePath} className="flex items-center justify-center">
          <img src={cfVerticalLogo} alt="CRAFTFINDUS" className={collapsed && isDesktop ? "h-24 w-auto" : "h-36 w-auto"} />
        </LocaleLink>
      </div>

      {/* 2️⃣ User Block */}
      <button
        onClick={() => profilePath && navigate(profilePath)}
        className={cn(
          "flex items-center gap-3 border-b border-border transition-colors hover:bg-secondary/30",
          isDesktop && collapsed ? "p-3 justify-center" : "p-5"
        )}
      >
        <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium shrink-0">
          {initials}
        </div>
        {(!isDesktop || !collapsed) && (
          <div className="text-left overflow-hidden">
            <p className="text-sm text-foreground truncate leading-tight">{displayName}</p>
            <p className="text-micro mt-0.5">{roleLabel}</p>
          </div>
        )}
      </button>

      {/* 3️⃣ Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <LocaleLink
              key={item.path}
              to={item.path}
              title={isDesktop && collapsed ? item.label : undefined}
              className={cn(
                "group relative flex items-center gap-3 rounded-sm text-sm transition-all duration-200",
                isDesktop && collapsed ? "px-3 py-2.5 justify-center" : "px-4 py-2.5",
                active
                  ? "text-foreground bg-accent/8"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              )}
            >
              {/* Active indicator bar */}
              {active && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r-full" />
              )}
              <Icon className="w-[18px] h-[18px] shrink-0" />
              {(!isDesktop || !collapsed) && <span>{item.label}</span>}

              {/* Tooltip for collapsed desktop */}
              {isDesktop && collapsed && (
                <span className="absolute left-full ml-2 px-2.5 py-1 bg-card border border-border text-foreground text-xs rounded-sm whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-md z-50">
                  {item.label}
                </span>
              )}
            </LocaleLink>
          );
        })}
      </nav>

      {/* 4️⃣ CTA (optional) */}
      {cta && (!isDesktop || !collapsed) && (
        <div className="px-3 pb-3">
          <button
            onClick={cta.onClick}
            className="w-full p-3 border border-border bg-secondary/30 hover:bg-secondary/50 rounded-sm transition-colors text-left"
          >
            <div className="flex items-center gap-2 mb-1">
              <cta.icon className="w-4 h-4 text-accent" />
              <span className="text-xs font-medium text-foreground">{cta.label}</span>
            </div>
            <p className="text-micro leading-normal">{cta.description}</p>
          </button>
        </div>
      )}

      {/* 5️⃣ Footer */}
      <div className="border-t border-border relative" ref={avatarRef}>
        {isDesktop && collapsed ? (
          <div className="p-2 flex flex-col items-center gap-1">
            <button
              onClick={() => setAvatarOpen(!avatarOpen)}
              className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              title="Menü"
            >
              <User className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="p-3 space-y-1">
            {portalSwitchPath && (
              <button
                onClick={() => navigate(portalSwitchPath)}
                className="flex items-center gap-3 w-full px-3 py-2 text-sm text-accent hover:bg-accent/5 rounded-sm transition-colors"
              >
                <ArrowRightLeft className="w-4 h-4" />
                <span>{portalSwitchLabel || t("portal.switchPortal")}</span>
              </button>
            )}
            {profilePath && (
              <button
                onClick={() => navigate(profilePath)}
                className="flex items-center gap-3 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-sm transition-colors"
              >
                <User className="w-4 h-4" />
                <span>{t("portal.profile")}</span>
              </button>
            )}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-sm transition-colors"
            >
              <KeyRound className="w-4 h-4" />
              <span>{t("portal.changePassword")}</span>
            </button>
            <button
              onClick={() => signOut().then(() => navigate("/"))}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/5 rounded-sm transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t("portal.signOut")}</span>
            </button>
          </div>
        )}

        {/* Collapsed footer dropdown */}
        {isDesktop && collapsed && avatarOpen && (
          <div className="absolute left-full bottom-0 ml-2 bg-card border border-border shadow-lg rounded-sm py-1 min-w-[180px] z-50">
            {profilePath && (
              <button
                onClick={() => { setAvatarOpen(false); navigate(profilePath); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              >
                <User className="w-4 h-4" />
                {t("portal.profile")}
              </button>
            )}
            <button
              onClick={() => { setAvatarOpen(false); setShowPasswordModal(true); }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            >
              <KeyRound className="w-4 h-4" />
              {t("portal.changePassword")}
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => signOut().then(() => navigate("/"))}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/5 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t("portal.signOut")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* ── Desktop Sidebar (sticky, not overlay) ────────── */}
      <aside
        className={cn(
          "hidden md:flex fixed top-0 left-0 h-screen z-40 border-r border-border bg-card flex-col transition-all duration-300 ease-in-out",
          collapsed ? "w-[72px]" : "w-[272px]"
        )}
      >
        {sidebarContent(true)}
      </aside>

      {/* ── Mobile hamburger ─────────────────────────────── */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-sm bg-card border border-border text-muted-foreground hover:text-foreground shadow-md transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* ── Mobile drawer ────────────────────────────────── */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-foreground/10 backdrop-blur-[2px]"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="md:hidden fixed top-0 left-0 h-full w-[272px] z-50 border-r border-border bg-card shadow-xl animate-fade-in">
            {sidebarContent(false)}
          </aside>
        </>
      )}

      {/* ── Password Modal ───────────────────────────────── */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/20 backdrop-blur-sm">
          <div className="bg-card border border-border p-8 w-full max-w-md space-y-6">
            <h2 className="text-subheadline">{t("portal.changePassword")}</h2>
            <div>
              <label className="text-micro block mb-2">{t("portal.newPassword")}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("portal.passwordPlaceholder")}
                className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handlePasswordChange}
                disabled={passwordSaving || newPassword.length < 6}
                className="flex-1 h-10 bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {passwordSaving ? t("portal.saving") : t("portal.saveBtn")}
              </button>
              <button
                onClick={() => { setShowPasswordModal(false); setNewPassword(""); }}
                className="h-10 px-6 border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("portal.cancelBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
